import {
  OpenCopilotChatEvent,
  publishOpenCopilotChat,
  subscribeOpenCopilotChat,
  subscribePreloadCopilotChat,
} from '@github-ui/copilot-chat-events'
import {getDockedPanelOwner} from '@github-ui/copilot-chat-helpers/constants'
import {usePersistedChatOpenState} from '@github-ui/copilot-chat-helpers/embedded-chat-open-state'
import {CopilotChatIntents} from '@github-ui/copilot-chat-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {GlobalCopilotMenu} from '@github-ui/global-copilot-menu/GlobalCopilotMenu'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {CommentDiscussionIcon, CopilotIcon} from '@primer/octicons-react'
import {ButtonGroup} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect, useRef, useState} from 'react'

import {NavLinkItem} from '../../primitives/NavItemLink'
import baseStyles from '../../primitives/styles.module.css'
import {useShouldLoadCopilotChat, useTopNavConfig} from '../../primitives/TopNav'
import {useGlobalNavAnalytics} from '../../utils/Analytics'
import {LazyGlobalNavAgentsMenu} from '../LazyGlobalNavAgentsMenu'
import {CopilotChatLoader} from './CopilotChatLoader'
import styles from './CopilotItems.module.css'

export type CopilotItemsProps = {
  show?: boolean
  showAgentsButton?: boolean
  copilotApiUrl?: string | undefined
  copilotChatUrl?: string
}

export function CopilotItems({show, showAgentsButton, copilotApiUrl, copilotChatUrl}: CopilotItemsProps) {
  const shouldLoadCopilotChat = useShouldLoadCopilotChat()
  const {loadCopilotChat} = useTopNavConfig()
  const {sendItemClick} = useGlobalNavAnalytics('COPILOT')
  const isImmersiveEmbeddedHeaderButton = isFeatureEnabled('copilot_immersive_embedded_header_button')
  const isDeferredPayload =
    isImmersiveEmbeddedHeaderButton && isFeatureEnabled('copilot_immersive_embedded_deferred_payload')
  const isChatAppEmbedded = isFeatureEnabled('copilot_chat_app_embedded')
  const shouldShowEmbeddedChatButton = isChatAppEmbedded && Boolean(copilotChatUrl)

  // Defer the Copilot chat loader to the client. `useRuntimeEnvironment` returns the server
  // snapshot during SSR and the first hydration render, then re-renders as `isBrowser` on the
  // client — so the loader (and its data fetch) never runs on the server or causes a hydration
  // mismatch.
  const {isBrowser} = useRuntimeEnvironment()
  // Store pending open event to re-dispatch after embedded component loads
  const [pendingOpenEvent, setPendingOpenEvent] = useState<OpenCopilotChatEvent | null>(null)
  // Controls the ChatAppEmbedded panel independently from the CopilotImmersiveEmbedded panel
  const [persistedChatAppOpen, setChatAppOpen] = usePersistedChatOpenState()
  const chatAppOpen = isChatAppEmbedded && persistedChatAppOpen
  const [hasUserOpenedChatApp, setHasUserOpenedChatApp] = useState(false)
  const isRestoredChatAppOpen = chatAppOpen && !hasUserOpenedChatApp
  const embeddedChatTriggerRef = useRef<HTMLAnchorElement | HTMLButtonElement | null>(null)
  const embeddedChatLauncherRef = useRef<HTMLDivElement>(null)

  // Subscribe to open-copilot-chat events to trigger loading.
  useEffect(() => {
    return subscribeOpenCopilotChat(e => {
      loadCopilotChat()
      // When the chat-app-embedded flag is on, page-specific buttons (PR header,
      // explain file, etc.) should open the ChatApp UI. Only the global copilot
      // button (source: 'header') continues to open the immersive UI.
      // On docked pages, whichever UI claims the panel first stays permanently;
      // subsequent opens just reopen the same panel.
      if (isChatAppEmbedded && e.payload.source !== 'header') {
        setChatAppOpen(true)
        setPendingOpenEvent(e)
      } else {
        // Only clear `chatAppOpen` when chatapp hasn't already claimed the
        // retained docked-panel slot. Once chatapp owns the claim the
        // immersive UI won't take it over (see `getDockedPanelOwner()`
        // gate in `DockedCopilotChatPortal`), so hiding chatapp here would
        // leave a header-button reopen with an empty `#copilot-chat-docked-content`
        // element — no surface portals into it.
        if (getDockedPanelOwner() !== 'chatapp') {
          setChatAppOpen(false)
        }
        setPendingOpenEvent(e)
      }
    })
  }, [loadCopilotChat, isChatAppEmbedded, setChatAppOpen])

  // Subscribe to preload-copilot-chat events to warm up the chat component without opening it.
  useEffect(() => {
    return subscribePreloadCopilotChat(() => {
      loadCopilotChat()
    })
  }, [loadCopilotChat])

  if (!show) {
    return null
  }

  const handleCopilotIconClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    e.preventDefault()
    loadCopilotChat()
    publishOpenCopilotChat({
      id: 'copilot-assistive',
      intent: CopilotChatIntents.conversation,
      newThread: true,
      source: 'header',
    })
  }

  const handleOpenEmbeddedChat = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    e.preventDefault()
    embeddedChatTriggerRef.current = e.currentTarget
    setHasUserOpenedChatApp(true)
    loadCopilotChat()
    // Publish an event so the side panel integration (on docked pages) can
    // intercept it and open the docked panel. The subscribeOpenCopilotChat
    // handler routes non-header sources to setChatAppOpen.
    publishOpenCopilotChat({
      id: 'copilot-chat-app',
      intent: CopilotChatIntents.conversation,
    })
  }

  const copilotItemProps = {
    href: '/copilot',
    icon: CopilotIcon,
    'aria-label': 'Chat with Copilot',
    analyticsKey: 'COPILOT_CHAT',
    analyticsSubCategory: 'COPILOT',
    copilotApiUrl,
    onClick: handleCopilotIconClick,
  }

  return (
    <div className="hide-sm hide-md">
      <div className={styles.Wrapper}>
        <ButtonGroup>
          <NavLinkItem {...copilotItemProps} />
          {isImmersiveEmbeddedHeaderButton ? (
            <GlobalCopilotMenu
              anchorProps={{
                variant: 'invisible',
                className: clsx(baseStyles.appHeaderButton, styles.CopilotMenu),
                onMouseEnter: loadCopilotChat,
                onFocus: loadCopilotChat,
                onClick: () => {
                  loadCopilotChat()
                  sendItemClick('COPILOT_MENU')
                },
              }}
            />
          ) : showAgentsButton ? (
            <LazyGlobalNavAgentsMenu
              copilotApiUrl={copilotApiUrl}
              anchorProps={{
                variant: 'invisible',
                className: clsx(baseStyles.appHeaderButton, styles.AgentsMenu),
                onClick: () => {
                  sendItemClick('AGENTS_MENU')
                },
              }}
            />
          ) : (
            <GlobalCopilotMenu
              anchorProps={{
                variant: 'invisible',
                className: clsx(baseStyles.appHeaderButton, styles.CopilotMenu),
                onMouseEnter: loadCopilotChat,
                onFocus: loadCopilotChat,
                onClick: () => {
                  loadCopilotChat()
                  sendItemClick('COPILOT_MENU')
                },
              }}
            />
          )}
        </ButtonGroup>
        {shouldShowEmbeddedChatButton && (
          <div ref={embeddedChatLauncherRef} style={{display: 'contents'}}>
            <NavLinkItem
              icon={CommentDiscussionIcon}
              aria-label="Open Copilot Chat"
              analyticsKey="COPILOT_EMBEDDED_CHAT"
              analyticsSubCategory="COPILOT"
              onClick={handleOpenEmbeddedChat}
              onMouseEnter={loadCopilotChat}
              onFocus={loadCopilotChat}
            />
          </div>
        )}
        {isImmersiveEmbeddedHeaderButton && showAgentsButton && (
          <LazyGlobalNavAgentsMenu
            copilotApiUrl={copilotApiUrl}
            anchorProps={{
              variant: 'invisible',
              className: clsx(baseStyles.appHeaderButton, styles.AgentsMenu, styles.AgentsMenuStandalone),
              onClick: () => {
                sendItemClick('AGENTS_MENU')
              },
            }}
          />
        )}
      </div>
      {/*
        The chat loader is gated on `isBrowser` so it (and its data fetch) never runs on the server
        or during the first hydration render, keeping this subtree hydration-safe. `isDeferredPayload`
        can be true from feature flags during SSR, so without this client gate the loader would fetch
        on the server.
      */}
      {isBrowser && copilotChatUrl && (isDeferredPayload || shouldLoadCopilotChat || chatAppOpen) ? (
        <CopilotChatLoader
          copilotChatUrl={copilotChatUrl}
          pendingOpenEvent={pendingOpenEvent}
          onPendingEventHandled={() => setPendingOpenEvent(null)}
          chatAppOpen={chatAppOpen}
          onChatAppOpen={() => setChatAppOpen(true)}
          onChatAppClose={() => {
            setChatAppOpen(false)
            const fallbackFocusTarget = embeddedChatLauncherRef.current?.querySelector<HTMLButtonElement>('button')
            ;(embeddedChatTriggerRef.current ?? fallbackFocusTarget)?.focus()
          }}
          disableAutoFocus={isRestoredChatAppOpen}
        />
      ) : null}
    </div>
  )
}
